import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { config } from 'env';
import useAuth from 'hooks/useAuth';
import { useUserStripeState } from 'hooks/useUserStripe';
import StripeAccountUnavailable from 'components/StripeAccountUnavailable';
import { useBillingClient } from 'client/client-hook';
import { CardSetUpIntentReply } from 'proto/billing-manager_pb';
import { CHECKOUT_COPY } from 'lib/billing';
import { NotePanel } from '../NotePanel';

const CARD_COPY = CHECKOUT_COPY.payment.card;

export type StripeCardHandle = {
  confirmCard: () => Promise<string | null>;
  confirmPaymentIntent: (clientSecret: string, paymentMethodId?: string) => Promise<boolean>;
};

type Props = {
  onReady?: () => void;
  onError?: (msg: string) => void;
  onChange?: (complete: boolean) => void;
};

const InnerCard = forwardRef<
  StripeCardHandle,
  { onReady?: () => void; onError?: (m: string) => void; onChange?: (complete: boolean) => void }
>(function InnerCard({ onReady, onError, onChange }, ref) {
  const stripe = useStripe();
  const elements = useElements();

  useImperativeHandle(
    ref,
    () => ({
      async confirmCard() {
        if (!stripe || !elements) return null;
        const res = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: `${config.hostname}/account-settings/billing` },
          redirect: 'if_required'
        });
        if (res.error) {
          onError?.(res.error.message || CARD_COPY.setupFailed);
          return null;
        }
        const si: any = res.setupIntent;
        if (si?.status !== 'succeeded') {
          onError?.(CARD_COPY.setupIncomplete);
          return null;
        }
        return (si.payment_method as string) || null;
      },
      async confirmPaymentIntent(clientSecret: string, paymentMethodId?: string) {
        if (!stripe) return false;
        const res = await (stripe as any).confirmCardPayment(
          clientSecret,
          paymentMethodId ? { payment_method: paymentMethodId } : undefined
        );
        if (res.error) {
          onError?.(res.error.message || CARD_COPY.confirmFailed);
          return false;
        }
        return res.paymentIntent?.status === 'succeeded' || res.paymentIntent?.status === 'processing';
      }
    }),
    [stripe, elements, onError]
  );

  return (
    <Box>
      <PaymentElement onReady={() => onReady?.()} onChange={(e: any) => onChange?.(!!e?.complete)} />
    </Box>
  );
});

export const StripeCardElement = forwardRef<StripeCardHandle, Props>(function StripeCardElement({ onReady, onError, onChange }, ref) {
  const { user } = useAuth() as any;
  const { stripe, error: stripeError, retryable, retry } = useUserStripeState();
  const getBillingClient = useBillingClient();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!user?.tcId) return;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const client = await getBillingClient();
        const resp = (await client.cardSetUpIntent({ userId: user.tcId })) as CardSetUpIntentReply.AsObject;
        if (cancelled) return;
        setClientSecret(resp?.clientSecret || null);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || CARD_COPY.initFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.tcId, getBillingClient]);

  // No publishable key could be resolved, so Stripe.js was deliberately not
  // loaded. Say so instead of spinning forever.
  if (stripeError) {
    return <StripeAccountUnavailable error={stripeError} retryable={retryable} onRetry={retry} sx={{ my: 0 }} />;
  }
  if (loading || !stripe) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {CARD_COPY.loading}
        </Typography>
      </Stack>
    );
  }
  if (err || !clientSecret) {
    return <NotePanel severity="error" body={err || CARD_COPY.unavailable} />;
  }

  return (
    <Elements stripe={stripe} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <InnerCard ref={ref} onReady={onReady} onError={onError} onChange={onChange} />
    </Elements>
  );
});
